import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { Auction, Bid, Profile } from '../types';
import { formatCurrency, formatDateTime, participantLabel } from '../lib/format';
import { EmptyState } from '../components/EmptyState';

type ClosedAuction = Auction & { winner?: Profile | null; topBids?: Bid[] };

export default function HistoryPage() {
  const [auctions, setAuctions] = useState<ClosedAuction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('auctions')
        .select('*')
        .eq('status', 'closed')
        .order('created_at', { ascending: false });
      if (error) console.error(error);

      const enriched = await Promise.all((data || []).map(async (auction) => {
        const [winnerResult, bidsResult] = await Promise.all([
          auction.current_winner_id
            ? supabase.from('profiles').select('*').eq('id', auction.current_winner_id).maybeSingle()
            : Promise.resolve({ data: null }),
          supabase.from('bids').select('*, profiles(display_name,nickname,avatar_url)').eq('auction_id', auction.id).order('created_at', { ascending: false }).order('amount', { ascending: false }).limit(5)
        ]);
        return { ...(auction as Auction), winner: winnerResult.data as Profile | null, topBids: (bidsResult.data || []) as Bid[] };
      }));

      setAuctions(enriched);
      setLoading(false);
    };
    load();
  }, []);

  const groups = useMemo(() => {
    return auctions.reduce<Record<string, ClosedAuction[]>>((acc, auction) => {
      const year = new Date(auction.created_at).getFullYear().toString();
      acc[year] = acc[year] || [];
      acc[year].push(auction);
      return acc;
    }, {});
  }, [auctions]);

  const years = Object.keys(groups).sort((a, b) => Number(b) - Number(a));

  return (
    <div className="page-stack">
      <section className="page-heading">
        <span className="eyebrow">Archivio</span>
        <h1>Storico aste concluse</h1>
      </section>

      {loading && <div className="screen-center small">Caricamento storico…</div>}
      {!loading && auctions.length === 0 && <EmptyState title="Storico vuoto" body="Quando un’asta si chiude, apparirà qui con vincitore, importo finale e cronologia." />}

      {years.map((year) => (
        <section key={year} className="history-year-group">
          <h2>{year}</h2>
          <div className="history-list">
            {groups[year].map((auction) => (
              <Link key={auction.id} to={`/auctions/${auction.id}`} className="panel history-card">
                <div>
                  <h2>{auction.title}</h2>
                  <p className="muted">Creata il {formatDateTime(auction.created_at)} · Chiusa il {formatDateTime(auction.closes_at)}</p>
                  <p>Vincitore: <strong>{auction.winner ? participantLabel(auction.winner) : 'Nessuno'}</strong></p>
                  {auction.topBids?.length === 0 && <p className="muted">Storico rilanci dettagliato archiviato.</p>}
                </div>
                <div className="history-price">{formatCurrency(auction.current_amount)}</div>
                <div className="mini-bids">
                  {auction.topBids?.map((bid) => <span key={bid.id}>{participantLabel(bid.profiles)} · {formatCurrency(bid.amount)}</span>)}
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
